<script setup lang="ts">
import { equipCollection } from '@/core/equip'
import { useUserStore } from '@/stores/modules/user'
import type { EquipItemType } from '@/types/equip'
import { computed, ref } from 'vue'
import { equipIcon, equipTypeList, getRiseStarAttr } from '../../item/data'
import { getRecastPrice, getRecastLockPrice } from '../data'
import { socketEmitAsync } from '@/utils/web-socket'
import { formatNumberWithCommas } from '@/utils'
import { ElMessage } from 'element-plus'
import type { RecastRes } from '@/types/smithy'
import { getEquipName } from '@/utils/equip'
import { DArrowRight, Lock, QuestionFilled, Unlock } from '@element-plus/icons-vue'
import type { UserAttrType } from '@/types/user'
import { BasicAttrTextEnum } from '@/enums'
import EquipInfo from '@/views/item/components/EquipInfo.vue'

const userStore = useUserStore()

const equipItemId = ref('')
const equipInfo = computed(() => {
  const res = userStore.itemList.find((item) => item.id === equipItemId.value)
  if (!res) {
    equipItemId.value = ''
    return undefined
  }
  const { name, type, attrs } = equipCollection[res.equipId]
  return { ...res, name, type, attrs }
})
const equippedList = computed(() =>
  equipTypeList.value.map((item) => {
    const itemId = userStore.equippedList[item]
    const equipInfo = userStore.itemList.find((item) => item.id === itemId)
    if (!equipInfo) return undefined
    const { name, type, attrs } = equipCollection[equipInfo.equipId]
    return { ...equipInfo, name, type, attrs }
  }),
)

const itemList = computed<EquipItemType[]>(() => {
  const items = userStore.itemList
  if (userStore.itemList.length === 0) return []
  return items
    .filter((item) => {
      const equippedIdList = Object.values(userStore.equippedList)
      if ((item.starAttrs?.length || 0) < 5) return false
      return !equippedIdList.includes(item.id)
    })
    .map((item) => {
      const { equipId } = item
      const { name, type, attrs } = equipCollection[equipId]
      return { ...item, name, type, attrs }
    })
})

const isRecasting = ref(false)
function handleChoice(item: EquipItemType | undefined) {
  if (item && (item?.starAttrs?.length || 0) < 5) return ElMessage.warning('仅有满星的装备可重铸')
  equipItemId.value = item?.id || ''
  isRecasting.value = item ? true : false
}

const lockIndexList = ref<number[]>([])
function handleLockIndexList(index: number) {
  const curIndex = lockIndexList.value.findIndex((item) => item === index)
  if (curIndex === -1) {
    if (lockIndexList.value.length >= 4) return ElMessage.warning('最多锁定4个词条')
    lockIndexList.value.push(index)
  } else {
    lockIndexList.value.splice(curIndex, 1)
  }
}

const recastResShow = ref(false)
const oldStarAttrs = ref<number[]>()
function handleRecast() {
  if (!equipInfo.value) return
  const { id, quality } = equipInfo.value
  const goldPrice = getRecastPrice(quality)
  const diamondPrice = getRecastLockPrice(lockIndexList.value.length - 1)
  if (goldPrice > userStore.gold) {
    return ElMessage.warning('您的金币不足')
  }
  if (diamondPrice > userStore.diamond) {
    return ElMessage.warning('您的钻石不足')
  }
  ElMessageBox.confirm('<p class="affix">重铸操作将会对您<span class="info">未锁定</span>的升星属性随机进行属性变化，是否确认重铸？</p>', '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      const { starAttrs, userAttr } = await socketEmitAsync<RecastRes>({
        type: 'recast',
        data: { id: equipInfo.value?.id, lockIndexList: lockIndexList.value },
      })

      const item = userStore.itemList.find((item) => item.id === id)
      if (!item || !equipInfo.value || !equipInfo.value.starAttrs) return
      oldStarAttrs.value = [...equipInfo.value.starAttrs]
      item.starAttrs = starAttrs
      equipInfo.value.starAttrs = starAttrs

      userStore.gold -= goldPrice
      userStore.diamond -= diamondPrice
      userStore.userAttr = userAttr

      recastResShow.value = true
    })
    .catch(() => {})
}
</script>

<template>
  <div class="rise-star">
    <div class="rise-star-table border-wrap">
      <h4>锻造台</h4>
      <div class="rise-star-equip">
        <CommonBtn style="width: 100%" @done="handleChoice(undefined)">
          <span v-if="equipInfo" v-html="getEquipName(equipInfo, equipInfo.type)"></span>
          <span v-else>(未放入)</span>
        </CommonBtn>
      </div>
      <div class="help-info">
        <el-popover placement="bottom" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>重铸说明</h4>
            <p>1. 重铸为<span class="info">满星</span>的装备提供升星属性重新随机的功能。</p>
            <p>2. 每次重铸都将对未锁定的升星属性中进行随机的属性改变。</p>
            <p>3. 您可以花费<span class="diamond">钻石</span>锁定部分属性，这样在重铸时，已锁定的属性不会发生变化。</p>
            <p>4. 当您锁定4条属性后，重铸时不会发生无变化的情况。</p>
          </div>
        </el-popover>
      </div>
      <template v-if="equipInfo">
        <div class="info-card">
          <p>重铸花费：{{ formatNumberWithCommas(getRecastPrice(equipInfo.quality)) }} 金币</p>
          <p>锁定花费：{{ formatNumberWithCommas(getRecastLockPrice(lockIndexList.length - 1)) }} 钻石</p>
        </div>
        <div class="btn-wrap">
          <el-button type="warning" plain @click="handleRecast" style="margin-bottom: 10px">开始重铸</el-button>
          <el-button type="danger" plain @click="handleChoice(undefined)">取消</el-button>
        </div>
      </template>
    </div>
    <template v-if="!isRecasting">
      <div class="item-list border-wrap" style="flex-shrink: 0; margin-bottom: 10px" v-show="!isRecasting">
        <h4>已装备</h4>
        <div class="item-btn-wrap btn-wrap">
          <el-row :gutter="10">
            <el-col :span="12" v-for="(item, index) of equippedList" :key="index">
              <CommonBtn
                size="small"
                @done="handleChoice(item)"
                :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }"
                :type="item ? 'primary' : 'info'"
              >
                <span v-if="item" v-html="getEquipName(item, item.type)"></span>
                <span v-else>{{ equipIcon[equipTypeList[index]] }} (未装备)</span>
              </CommonBtn>
            </el-col>
          </el-row>
        </div>
      </div>
      <div class="item-list border-wrap package">
        <h4>背包</h4>
        <p class="cur-gold">
          <span style="margin-right: 10px">💰 {{ formatNumberWithCommas(userStore.gold) }} </span>
          <span>💎 {{ formatNumberWithCommas(userStore.diamond) }}</span>
        </p>
        <div class="item-btn-wrap btn-wrap item-list-wrap">
          <el-row :gutter="10">
            <el-col :span="12" v-for="item of itemList" :key="item.id">
              <CommonBtn
                size="small"
                @done="isRecasting ? handleRecast() : handleChoice(item)"
                :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }"
              >
                <span v-html="getEquipName(item, item.type)"></span>
              </CommonBtn>
            </el-col>
          </el-row>
        </div>
        <p class="affix" style="text-align: center" v-show="!itemList.length">您的背包中不存在<span class="info">满星</span>的装备</p>
      </div>
    </template>
    <template v-else>
      <div class="recast-detail">
        <p class="cur-gold" style="margin-top: 0; justify-content: flex-end">
          <span style="margin-right: 10px">💰 {{ formatNumberWithCommas(userStore.gold) }} </span>
          <span>💎 {{ formatNumberWithCommas(userStore.diamond) }}</span>
        </p>
        <div class="border-wrap">
          <h4>当前升星属性</h4>
          <p v-for="(item, index) of getRiseStarAttr(equipInfo!.starAttrs!)">
            <el-button size="small" :type="lockIndexList.includes(index) ? 'success' : 'info'" plain @click="handleLockIndexList(index)">
              <el-icon size="14" v-show="!lockIndexList.includes(index)"><Unlock /></el-icon>
              <el-icon size="14" v-show="lockIndexList.includes(index)"><Lock /></el-icon>
            </el-button>
            {{ BasicAttrTextEnum[Object.keys(item)[0] as keyof UserAttrType] }}
            <span>
              +
              <span class="grow">
                {{ Object.values(item)[0] }}{{ ['crt', 'crtd', 'hr', 'atksp'].includes(Object.keys(item)[0] as keyof UserAttrType) ? '%' : '' }}
              </span>
            </span>
          </p>
        </div>
      </div>
    </template>

    <el-dialog v-model="recastResShow" width="100%" style="max-width: 500px">
      <h2 class="affix">
        <span class="grow">重铸成功</span>
      </h2>
      <template v-if="equipInfo">
        <el-popover placement="bottom" width="300px" trigger="click" :teleported="false">
          <template #reference>
            <div style="width: 50%; margin: 0 auto">
              <CommonBtn size="small" style="width: 100%">
                <span v-html="getEquipName(equipInfo, equipInfo.type)"></span>
              </CommonBtn>
            </div>
          </template>
          <EquipInfo :equip-info="equipInfo" :show-action="false" style="max-height: 60vh" />
        </el-popover>
      </template>
      <div class="res-compare" v-if="equipInfo && oldStarAttrs">
        <div class="border-wrap">
          <h4>重铸前</h4>
          <p v-for="item of getRiseStarAttr(oldStarAttrs)">
            {{ BasicAttrTextEnum[Object.keys(item)[0] as keyof UserAttrType] }}
            <span>
              +
              <span class="grow">
                {{ Object.values(item)[0] }}{{ ['crt', 'crtd', 'hr', 'atksp'].includes(Object.keys(item)[0] as keyof UserAttrType) ? '%' : '' }}
              </span>
            </span>
          </p>
        </div>
        <el-icon size="30"><DArrowRight /></el-icon>
        <div class="border-wrap">
          <h4>重铸后</h4>
          <p v-for="item of getRiseStarAttr(equipInfo.starAttrs!)">
            {{ BasicAttrTextEnum[Object.keys(item)[0] as keyof UserAttrType] }}
            <span>
              +
              <span class="grow">
                {{ Object.values(item)[0] }}{{ ['crt', 'crtd', 'hr', 'atksp'].includes(Object.keys(item)[0] as keyof UserAttrType) ? '%' : '' }}
              </span>
            </span>
          </p>
        </div>
      </div>
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="recastResShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
  </div>
</template>

<style lang="scss" scoped>
.rise-star {
  display: flex;
  flex-flow: column nowrap;
  overflow: hidden;
  .rise-star-table {
    position: relative;
    padding: 0 8px 10px 8px;
    margin-bottom: 10px;
    overflow: auto;
    .rise-star-equip {
      border: 1px var(--fontColor) solid;
      padding: 6px 8px;
      border-radius: 4px;
      background-color: rgba($color: var(--fontColor), $alpha: 0.1);
    }
    .help-info {
      position: absolute;
      top: 5px;
      right: 10px;
    }
  }
  .item-list {
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    justify-content: unset;
    :deep() {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
    .cur-gold {
      width: 100%;
      text-align: right;
      font-size: 14px;
      padding-right: 10px;
      margin: 0;
    }
    .item-btn-wrap {
      overflow: auto;
    }
    &.package {
      flex: 1;
      min-height: 135px;
    }
  }
  .recast-detail {
    flex: 1;
    .border-wrap {
      padding: 0 10px;
    }
    p {
      font-size: 14px;
      margin: 8px 0;
      display: flex;
      align-items: center;
      button {
        margin-right: 10px;
      }
    }
  }
  .res-compare {
    padding: 10px 0;
    display: flex;
    align-items: center;
    .border-wrap {
      padding: 5px;
      p {
        margin: 6px 0;
      }
    }
    i {
      margin: 0 10px;
    }
  }
  .btn-wrap {
    & > div {
      justify-content: unset;
      margin-bottom: 0;
    }
    .common-btn-wrap {
      margin-bottom: 10px;
    }
  }
  .el-button + .el-button {
    margin-left: 0;
  }
}
</style>
